import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useParams, useLocation } from "react-router-dom";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import "./RequestFlow.scss";

const STEP_KEYS = [
  { key: "upload", pathSeg: "upload", label: "Documents" },
  { key: "category", pathSeg: "category", label: "Category" },
  { key: "details", pathSeg: "details", label: "Details" },
  { key: "review", pathSeg: "review", label: "Review" },
];

/**
 * Linear shell: `/request/:id/...` with step strip (synced with workflow when available).
 */
export default function RequestFlowLayout() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const [summary, setSummary] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!id) return;
      try {
        const res = await apiRequest({ method: "GET", url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(id) });
        const w = res?.request?.requestWorkflowStatus;
        if (!cancel && w) setSummary(String(w));
      } catch {
        if (!cancel) setSummary("");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  const currentSeg = useMemo(() => {
    const m = pathname.match(/\/request\/[^/]+\/(\w+)/);
    return m ? m[1] : "upload";
  }, [pathname]);

  const currentIdx = useMemo(
    () => Math.max(0, STEP_KEYS.findIndex((s) => s.pathSeg === currentSeg)),
    [currentSeg]
  );
  const isCreateWizardStep = ["upload", "category", "details", "review"].includes(currentSeg);

  return (
    <PatientAppLayout activeNav="requests">
      <div className="reqFlowLayout reqFlowLayout--inApp">
        {!isCreateWizardStep ? (
          <>
            <header className="reqFlowLayout__intro">
              <p className="reqFlowLayout__eyebrow">Care request</p>
              <h1 className="reqFlowLayout__title">Request workflow</h1>
              {id ? <p className="reqFlowLayout__sub">ID {id.slice(0, 8)}…</p> : null}
            </header>
            <div className="reqFlowLayout__stepper" aria-label="Request progress">
              {summary ? (
                <span className="reqFlowLayout__statusPill">
                  {summary.replace(/_/g, " ")}
                </span>
              ) : null}
              <div className="reqFlowLayout__steps">
                {STEP_KEYS.map((s, i) => {
                  const isPast = i < currentIdx;
                  const isCurrent = i === currentIdx;
                  const isLocked = i > currentIdx;
                  const st = isPast ? "done" : isCurrent ? "current" : "locked";
                  return (
                    <React.Fragment key={s.key}>
                      {i > 0 ? <span className="reqFlowLayout__chev" aria-hidden>›</span> : null}
                      <span className="reqFlowLayout__step" data-state={st}>
                        {isPast ? "✓ " : isLocked ? "" : ""}
                        {isLocked && id ? (
                          <span className="reqFlowLayout__stepLabel">{s.label}</span>
                        ) : (
                          <Link to={id ? `/request/${id}/${s.pathSeg}` : "#"} className="reqFlowLayout__stepLink">
                            {s.label}
                          </Link>
                        )}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
        <div className={`reqFlowLayout__out${isCreateWizardStep ? " reqFlowLayout__out--upload" : ""}`}>
          <Outlet />
        </div>
      </div>
    </PatientAppLayout>
  );
}
